<script setup>
import { useGesture } from '@/composables/calendar/useGesture';
import { useDeviceDetect } from '@/composables/useDeviceDetect';
import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
import { DEV_MODE } from '@/config/dev-mode';
import IconArrow09 from '@/components/svg/IconArrow09.vue';
import ComLoginMessage from '@/components/mobile/_com_login_message.vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
// import { getSajuAnalysisData } from '@/api/personality'
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import fiveMixin from '@/composables/fiveMixin';
import {
  splitFirstSentence,
  splitBySentence,
  splitArrayByPipe,
  formatDoubleHash,
} from '@/composables/splitSentence';
import { useAnalysisStore } from '@/store/modules/analysis';
import ConfirmComponent from '@/components/common/Confirm.vue';
import AlertComponent from '@/components/common/Alert.vue';

const analysisStore = useAnalysisStore();

definePage({ meta: { layout: 'empty' } });
const emit = defineEmits(['close', 'handleRegSave', 'redirectToLogin']);
const props = defineProps({
  prSajuData: Object,
  prBirthData: Object,
  langMode: String,
  // five: Object,
  transData: Object,
});

const handleRegSave = (callback) => {
  emit('handleRegSave', callback);
};

const redirectToLogin = () => {
  emit('redirectToLogin', { redirectOption: 'reading' });
};

/** 디바이스 감지 */
const { isMobile } = useDeviceDetect();
const { ...five } = fiveMixin();
const route = useRoute();
const router = useRouter();
const currentPage = ref(0);
const infoPage = ref(false);

const dialogAlert = ref(null);
const dialogConfirm = ref(null);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');
const isDataLoading = ref(false);

/** 사주풀이 데이터 */
const sData = ref({});

let ilgan = null;
let fiveData = null;
let gg = null;
let twelve = null;
let sal = null;

/** 사주풀이 요청 데이터 */
const params = computed(() => {
  if ((!props.prSajuData || Object.keys(props.prSajuData).length === 0) && !sajuReadingData.value) {
    return null;
  }

  const sajuData = sajuReadingData.value || props.prSajuData;
  const birthData = sajuReadingData.value?.birthganji || props.prSajuData.birthganji;

  // baseCode 생성하기
  const codeValues = [
    birthData.sky.hour?.value !== '-' ? birthData.sky.hour.value : '-',
    birthData.earth.hour?.value !== '-' ? birthData.earth.hour.value : '-',
    birthData.sky.day.value,
    birthData.earth.day.value,
    birthData.sky.month.value,
    birthData.earth.month.value,
    birthData.sky.year.value,
    birthData.earth.year.value,
  ];
  const baseCode = codeValues.join('');

  const paramsObject = {
    baseCode: baseCode,
    lang: 'KOR',
    birth: sajuData.solar ?? null,
    userName: sajuData.inputdata.name ?? '', // TODO: '' 대신 analysisStore 활용
    bln: sajuData.transdata.lucknum ?? null,
    gender: Number(sajuData.inputdata.gender) ?? null,
    unknownTime: sajuData.inputdata.unknown_time ?? null,
    analysisTypeId: 8,
  };

  return paramsObject;
});

/** 일간 구하기 */
const ilganChar = computed(() => {
  if (ilganInfo.value !== '') return ilganInfo.value.char;
  else return props.prSajuData?.birthganji?.sky?.day?.value;
});
const ilganChnChar = computed(() => {
  return five.getHanjaTxt(ilganChar.value, 'sky');
});

const isUnknownTime = computed(() => {
  if (sajuReadingData.value) {
    return sajuReadingData.value.inputdata.unknown_time === 'Y';
  } else {
    return props.prSajuData?.inputdata?.unknown_time === 'Y';
  }
});

const ilganInfo = ref('');
const sajuReadingData = ref(null);

const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];
const accessToken = ref(!!userId.value || !!sessionStorage.getItem('X-Token'));

onMounted(async () => {
  readyToShow.value = true;
});

/** 사주풀이 데이터 요청 */
const fetchSajuDescData = async () => {
  if (!params.value) return;

  try {
    const result = await fetchBaseServiceAPI(params.value);
    sData.value = result.data;

    ilgan = createChapterParser(sData, '일간특성');
    fiveData = createChapterParser(sData, '오행분포', { usePipe: true });
    gg = createChapterParser(sData, '격국특성');
    twelve = createChapterParser(sData, '십이운성특성');
    sal = createChapterParser(sData, '십이신살특성');
  } catch (error) {
    console.error('fetchSajuDescData 실패', error);
  } finally {
    isDataLoading.value = false;
  }
};

watch(
  () => params.value,
  async (newParams) => {
    isDataLoading.value = true;
    if (newParams) await fetchSajuDescData();
  },
  { immediate: true },
);

/** 데이터 할당 */
function createChapterParser(dataRef, chapterKey, options = {}) {
  const data = computed(() => dataRef.value[chapterKey]);

  const split = computed(() => splitFirstSentence(data.value?.기본특성));
  const keySentence = computed(() => split.value.first);
  const description = computed(() => split.value.restSentences);
  const detail = computed(() => splitBySentence(data.value?.상세특성));
  const strength = computed(() => splitBySentence(data.value?.강점설명));

  // 오행 기본특성 분리
  const pipeSplit = options.usePipe
    ? computed(() => splitArrayByPipe(split.value.restSentences))
    : null;
  const before = options.usePipe ? computed(() => pipeSplit.value.before) : null;
  const after = options.usePipe ? computed(() => pipeSplit.value.after) : null;

  return {
    data,
    split,
    keySentence,
    description,
    detail,
    strength,
    before,
    after,
  };
}

/** 오행 첫 문장(이름 분리ver) */
const fiveKeySentenceHtml = computed(() => {
  return formatDoubleHash(fiveData.keySentence.value);
});

/** 일간 첫 문장 이름 분리ver */
const ilganKeysentenceHtml = computed(() => formatDoubleHash(ilgan.keySentence.value));

const readyToShow = ref(false);

const clickPrevPage = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

const clickNextPage = () => {
  if (currentPage.value > 6) return;
  else currentPage.value += 1;
};

const onSwipeLeft = () => {
  if (currentPage.value === 0 || currentPage.value > 6) return;
  else currentPage.value += 1;
};

const onSwipeRight = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

const openInfoPage = () => {
  currentPage.value = 1;
  infoPage.value = false;
};

/** 통합 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: onSwipeLeft, // 왼쪽 스와이프 → 다음 페이지 이동
  onSwipeRight: onSwipeRight, // 오른쪽 스와이프 → 이전 페이지 이동
  SWIPE_THRESHOLD: 150,
});

const clickStart = () => {
  infoPage.value = false;
  currentPage.value = 1;
};

/** 팝업 닫기 */
const close = () => {
  emit('close');

  setTimeout(() => {
    readyToShow.value = false;
  }, 500);
};

const resetPopup = () => {
  currentPage.value = 0;
  infoPage.value = false;
};

const login = () => {
  redirectToLogin();
};

const routeTab = () => {
  if (accessToken.value) {
    analysisStore.selectedMenu = '/overviewSajuSummary';

    if (route.params.id) {
      router.push(`/analysis/${route.params.id}`);
    } else {
      handleRegSave(() => router.push('/saju-service'));
    }
  } else {
    showDialogConfirm('로그인 후 이용해 주세요.', '', '로그인', () => redirectToLogin());
  }
};

const showAlert = ({ title }) => {
  dialogTitle.value = title;
  dialogText.value = '';
  dialogAlert.value.openAlertDialog();
};

const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm; // 함수로 전달

  dialogConfirm.value.openConfirmDialog();
};

const message1 = ref('로그인 🔒 하면<br />사주팔자 해석 🔎 을 무료로 볼 수 있어요!');
const message2 = ref('당신의 사주엔 아직 펼쳐지지 않은<br>더 많은 이야기들이 있습니다 😊');
const message3 = ref('지금 보신 해석은,<br>사주팔자 속 핵심 기운만 간단히 요약한<br>첫인상이에요.');
const message4 = ref('"왜 나는 이런 기운을 가졌을까?"<br>"앞으로의 흐름은 어떻게 흘러갈까?"');
</script>

<template>
  <div
    class="pop-saju-reading-container"
    :class="{ mobile: isMobile }"
    @mousedown="handleStart"
    @mousemove="handleMove"
    @mouseup="handleEnd"
    @touchstart="handleStart"
    @touchmove="handleMove"
    @touchend="handleEnd"
  >
    <div
      v-if="currentPage === 0 && readyToShow && isUnknownTime"
      class="pop-saju-reading-inner intro"
    >
      <div class="pop-header__large intro">
        <button @click="close"><i class="pop-header__close"></i></button>
        <div class="pop-header__content">
          <div class="pop-header__icon">🔎️️</div>
          <div class="pop-header__text">
            <div class="pop-header__title">시간 정보가 필요해요</div>
          </div>
        </div>
      </div>
      <div class="pop-body intro" :class="isMobile ? 'pd-16' : 'pd-40'">
        <div class="pop-body__inner">
          <div class="img-0101-box">
            <div class="img-0101"></div>
          </div>
          <div class="sub-title-txt-box">
            정확한 해석을 위해<br />
            출생 시간 정보가 필요합니다.
          </div>

          <div class="pop-body__content only-mobile">
            <div class="pop-body__desc">
              <IconArrow09 />
              시간 정보가 없으면 사주의 구조가 달라지기 때문에 시간 모름 사주는 해석이 어렵습니다.
            </div>
            <div class="pop-body__desc">
              낭만만세력에서는 만세력 조회는 가능하지만 정확도를 위해
              <span class="strong">시간 모름의 경우 사주풀이 서비스 제공이 제한</span>됩니다.
              <br />이용에 양해 부탁드립니다. 감사합니다.
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="currentPage === 0 && readyToShow && !isUnknownTime"
      class="pop-saju-reading-inner intro"
    >
      <div class="pop-header__large intro">
        <button @click="close"><i class="pop-header__close"></i></button>
        <div class="pop-header__content">
          <div class="pop-header__icon">🔎️️</div>
          <div class="pop-header__text">
            <div class="pop-header__title">사주풀이</div>
          </div>
        </div>
      </div>
      <div class="pop-body intro" :class="isMobile ? 'pd-16' : 'pd-40'">
        <div class="pop-body__inner">
          <div class="img-0101-box">
            <div class="img-0101"></div>
          </div>
          <div class="sub-title-txt-box">
            사주 조회는 했는데 일간, 십성, 십이운성, 대운...<br />
            도무지 무슨 말인지 모르겠어요...
          </div>

          <div class="pop-body__content only-mobile">
            <div class="pop-body__desc">
              <IconArrow09 />
              사주 결과를 처음 접하면 낯선 용어들과 복잡한 구조에 당황스러운 마음이 드는 건
              자연스러워요. 무슨 뜻인지 몰라서, 그냥 화면을 닫아버렸던 마음은 결코 당신만의 경험은
              아니예요.
            </div>
            <div class="pop-body__desc">
              하지만 그 생소한 단어들 속에는 <br />
              <span class="strong">'나는 어떤 사람인지'</span> <br />
              <span class="strong">'지금 어떤 흐름 위에 서 있는지'</span> 를<br />
              들여다 볼 수 있는 중요한 해석이 담겨 있어요!
            </div>
            <div class="pop-body__desc">
              그래서 낭만만세력은 전문 용어를 가능한 쉽게 풀고, 사주를 일상의 언어로 함께 해석해
              가고자 해요.
            </div>
          </div>

          <div class="pop-body__content only-pc st-reading-first">
            <div class="pop-body__desc-inner">
              <IconArrow09 />
              <div class="pop-body__desc">
                사주 결과를 처음 접하면 낯선 용어들과 복잡한 구조에 당황스러운 마음이 드는 건
                자연스러워요. 무슨 뜻인지 몰라서, 그냥 화면을 닫아버렸던 마음은 결코 당신만의 경험은
                아니예요.
              </div>
              <div class="pop-body__desc">
                하지만 그 생소한 단어들 속에는
                <span class="strong">'나는 어떤 사람인지'</span>
                <span class="strong">'지금 어떤 흐름 위에 서 있는지'</span> 를<br />
                들여다 볼 수 있는 중요한 해석이 담겨 있어요!
              </div>
              <div class="pop-body__desc">
                그래서 낭만만세력은 전문 용어를 가능한 쉽게 풀고, 사주를 일상의 언어로 함께 해석해
                가고자 해요.
              </div>
            </div>
          </div>
        </div>

        <button
          :style="{ marginBottom: `calc(16px + var(--safe-area-bottom, 0px))` }"
          class="start-button"
          @click="openInfoPage"
        >
          바로 시작하기
        </button>
      </div>
    </div>

    <div v-if="currentPage !== 0 && readyToShow" class="pop-saju-reading-inner gray100">
      <div class="pop-header__small">
        <button @click="close"><i class="pop-header__close"></i></button>
        <div class="pop-header__content">
          <div class="pop-header__title_2">
            <i class="icon-search only-pc">🔎️️</i>
            <span v-if="currentPage === 1">'"사주, 나도 금방 이해할 수 있어요"</span>
            <span v-else
              ><span>{{ params?.userName }}</span
              >님의 사주풀이</span
            >
          </div>
        </div>
      </div>
      <template v-if="isDataLoading">
        <div class="pop-body mobile word-dictionary">
          <div class="popup-loading__container">
            <div class="popup-loading__img"></div>
            <div>분석중입니다!</div>
          </div>
        </div>
      </template>
      <template v-else>
        <div v-if="currentPage === 1" class="pop-body mobile word-dictionary">
          <div class="word-item-wrap">
            <div class="word-item-box">
              <div class="word-item-title-box">
                <div class="title">天</div>
                <div class="txt-box">
                  <div class="txt1">천간</div>
                  <div class="txt2">하늘의 기운을 뜻합니다.</div>
                </div>
              </div>
              <div class="word-item-desc">
                일간을 포함한 사주 구성의 절반을 차지합니다.<br />
                열 가지 종류가 있고, 사람마다 어떤 천간을 타고났는지가 달라요.
              </div>
              <div class="word-item-desc bg">
                갑목은 우직한 큰 나무, 정화는 은은한 촛불처럼 각각의 성질을 가지고 있어요.
              </div>
            </div>
            <div class="word-item-box">
              <div class="word-item-title-box">
                <div class="title">地</div>
                <div class="txt-box">
                  <div class="txt1">지지</div>
                  <div class="txt2">땅의 기운을 뜻합니다.</div>
                </div>
              </div>
              <div class="word-item-desc">
                각각의 동물 띠(쥐·소·호랑이 등)와 계절, 시간의 흐름을 담고 있습니다.<br
                  v-if="!isMobile"
                />
                열두 가지 종류가 있으며, 일간과의 관계에 따라 다양한 감정과 흐름이 나타나요.
              </div>
              <div class="word-item-desc bg">
                자수는 겨울의 물, 오화는 여름의 불처럼 계절과 자연의 이미지를 담고 있어요.
              </div>
            </div>
            <div class="word-item-box">
              <div class="word-item-title-box">
                <div class="title">日</div>
                <div class="txt-box">
                  <div class="txt1">일간</div>
                  <div class="txt2">사주의 주인공입니다.</div>
                </div>
              </div>
              <div class="word-item-desc">
                열 가지 천간 중 어떤 기운으로 태어났는지를 말해요.<br />
                내가 어떤 성향을 타고 났는지, 기본 기질을 보여주는 핵심입니다.
              </div>
              <div class="word-item-desc bg">
                갑목은 큰 나무, 기토는 촉촉한 흙처럼 기질을 담고있습니다.
              </div>
            </div>
            <div class="word-item-box">
              <div class="word-item-title-box">
                <div class="title">五</div>
                <div class="txt-box">
                  <div class="txt1">오행분포</div>
                  <div class="txt2">내 안의 기운의 균형을 보여주는 지도와 같습니다.</div>
                </div>
              </div>
              <div class="word-item-desc">
                목(木), 화(火), 토(土), 금(金), 수(水) 이 다섯 가지 기운이 내 사주 안에 얼마나
                어떻게 담겼는지를 나타내요.
              </div>
              <div class="word-item-desc bg">
                "나는 어떤 기운에 가까울까?" 강한 기운과 부족한 기운을 보면, 스스로를 어떻게
                보완하면 좋을지도 알 수 있어요.
              </div>
            </div>
            <div class="word-item-box">
              <div class="word-item-title-box">
                <div class="title">十</div>
                <div class="txt-box">
                  <div class="txt1">십성</div>
                  <div class="txt2">내가 세상과 관계를 맺는 방식입니다.</div>
                </div>
              </div>
              <div class="word-item-desc">
                사람을 대할 때, 일할 때, 감정을 표현할 때<br v-if="isMobile" />
                나는 어떤 패턴을 자주 반복하는지를 보여줘요.
              </div>
              <div class="word-item-desc bg">
                식신이 많으면 표현형, 관성이 강하면 책임형 같은 해석을 담고 있습니다.
              </div>
            </div>
            <div class="word-item-box">
              <div class="word-item-title-box">
                <div class="title">運</div>
                <div class="txt-box">
                  <div class="txt1">십이운성</div>
                  <div class="txt2">인생의 흐름과 페이스를 나타냅니다.</div>
                </div>
              </div>
              <div class="word-item-desc">
                지금 내 기운은 오르고 있을까요? 잠시 쉬는 중일까요?<br v-if="!isMobile" />
                열 두 단계로 성장-학장-휴식-전환의 사이클을 보여주고 있습니다.
              </div>
              <div class="word-item-desc bg">
                지금의 나는 에너지가 강한 시기일까요! 조용히 준비하는 시기일까요?
              </div>
            </div>
            <div class="word-item-box">
              <div class="word-item-title-box">
                <div class="title">大</div>
                <div class="txt-box">
                  <div class="txt1">대운</div>
                  <div class="txt2">10년 단위로 흐르는 인생의 큰 계절과도 같습니다.</div>
                </div>
              </div>
              <div class="word-item-desc">
                누구에게나 인생의 봄･여름･가을･겨울이 오듯,<br v-if="isMobile" />
                사주에서는 10년 단위로 에너지의 흐름이 바뀝니다.
              </div>
              <div class="word-item-desc bg">
                지금 나는 어떤 계절을 지나고 있을까?<br />
                대운은 '변화의 타이밍', 즉 삶의 전환점을 알려주는 중요한 힌트가 됩니다.
              </div>
            </div>
          </div>

          <div class="word-btm-box">
            <div class="txt1">
              이제, 어렵게 느껴졌던 사주 키워드들이 <br />조금은 더 편안하고 익숙하게 다가오셨다면?
            </div>
            <div class="txt2">이제 사주팔자의 요약 해석을 만나보세요!</div>
          </div>
        </div>

        <div v-if="currentPage === 2" class="pop-body mobile">
          <div class="pop-body__inner page-content-box">
            <div class="pop-body__title">
              <div class="five-box">
                <span class="txt-round chnkor CHNKOR" :class="[five.getColorFive(ilganChnChar)]"
                  ><em class="chn">{{ ilganChnChar }}</em
                  ><em class="txt-main">{{ ilganChar }}</em></span
                >
              </div>
              <span>나는 어떤 사람일까? (사주로 보는 일간의 특성)</span>
            </div>
            <div v-if="ilgan" class="pop-body__content">
              <div
                class="pop-body__tit ganji-txt"
                :class="[five.getColorFive(ilganChnChar)]"
                v-html="ilganKeysentenceHtml"
              />
              <div class="pop-body__desc-wrap">
                <div class="tit">처음 만났을 때 느껴지는 당신</div>
                <div class="txt_wrap">
                  <div v-for="line in ilgan?.description.value" :key="line" class="txt">
                    {{ line }}
                  </div>
                </div>
              </div>
              <div class="default-wrap">
                <div class="pop-body__desc-wrap">
                  <div class="tit">겉으로 보이지 않는 마음의 흐름</div>
                  <div class="txt_wrap">
                    <div v-for="line in ilgan?.detail.value" class="txt">
                      {{ line }}
                    </div>
                  </div>
                </div>
                <div class="pop-body__desc-wrap">
                  <div class="tit">사람들이 놓치기 쉬운 당신의 매력</div>
                  <div class="txt_wrap none-bb">
                    <div v-for="line in ilgan?.strength.value" class="txt">
                      {{ line }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentPage === 3" class="pop-body mobile">
          <div class="pop-body__inner page-content-box">
            <div class="pop-body__title">
              <span class="icon">⭐️</span>
              <span class="title">오행의 분포!<br />당신의 중심에너지를 보여줍니다.</span>
            </div>
            <div v-if="fiveData" class="pop-body__content">
              <div
                class="pop-body__tit ganji-txt"
                :class="[five.getColorFive(ilganChnChar)]"
                v-html="fiveKeySentenceHtml"
              />

              <div class="pop-body__desc-wrap">
                <div class="tit">겉으로 드러나는 힘</div>
                <div class="txt_wrap">
                  <div
                    v-for="line in fiveData?.before.value"
                    :key="line"
                    class="txt"
                    v-html="formatDoubleHash(line)"
                  />
                </div>
              </div>

              <div class="default-wrap" v-if="accessToken">
                <div class="pop-body__desc-wrap">
                  <div class="tit">함께 작용하는 오행의 기운들</div>
                  <div class="txt_wrap">
                    <div
                      v-for="line in fiveData?.after.value"
                      :key="line"
                      class="txt"
                      v-html="formatDoubleHash(line)"
                    />
                  </div>
                </div>

                <div class="pop-body__desc-wrap">
                  <div class="tit">안에 깃든 에너지</div>
                  <div class="txt_wrap none-bb">
                    <div
                      v-for="line in fiveData?.detail.value"
                      :key="line"
                      class="txt"
                      v-html="formatDoubleHash(line)"
                    />
                  </div>
                </div>
              </div>
              <div class="default-wrap" v-else>
                <ComLoginMessage
                  :message="message1"
                  :customClass="isMobile ? 'bg2' : 'bg3'"
                  :showBtn="true"
                  @login="login"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentPage === 4" class="pop-body mobile">
          <div class="pop-body__inner page-content-box">
            <div class="pop-body__title">
              <span class="icon">🙂</span>
              <span class="title">관계속에서의 내 스타일(격국으로 보는 나)</span>
            </div>
            <div class="pop-body__content">
              <div class="pop-body__tit ganji-txt" :class="[five.getColorFive(ilganChnChar)]">
                {{ gg?.keySentence }}
              </div>
              <div class="pop-body__desc-wrap">
                <div class="tit">관계 속에서 만나는 나</div>
                <div class="txt_wrap">
                  <div v-for="line in gg?.description.value" :key="line" class="txt">
                    {{ line }}
                  </div>
                </div>
              </div>
              <div class="default-wrap" v-if="accessToken">
                <div class="pop-body__desc-wrap">
                  <div class="tit">나를 이끄는 기운의 틀</div>
                  <div class="txt_wrap none-bb">
                    <div v-for="line in gg?.detail.value" :key="line" class="txt">
                      {{ line }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="default-wrap" v-else>
                <ComLoginMessage
                  :message="message2"
                  :customClass="isMobile ? 'bg2' : 'bg3'"
                  @login="login"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentPage === 5" class="pop-body mobile">
          <div class="pop-body__inner page-content-box">
            <div class="pop-body__title">
              <span class="icon">🤝</span>
              <span class="title">십이운성으로 보는 '나의 성향과 스타일'</span>
            </div>
            <div class="pop-body__content">
              <div class="pop-body__tit ganji-txt" :class="[five.getColorFive(ilganChnChar)]">
                {{ twelve?.keySentence }}
              </div>

              <div class="pop-body__desc-wrap">
                <div class="tit">나의 성향과 표현 방식</div>
                <div class="txt_wrap">
                  <div v-for="line in twelve?.description.value" :key="line" class="txt">
                    {{ line }}
                  </div>
                </div>
              </div>
              <div class="default-wrap" v-if="accessToken">
                <div class="pop-body__desc-wrap">
                  <div class="tit">나의 반응과 성장</div>
                  <div class="txt_wrap none-bb">
                    <div v-for="line in twelve?.detail.value" :key="line" class="txt">
                      {{ line }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="default-wrap" v-else>
                <ComLoginMessage
                  :message="message3"
                  :customClass="isMobile ? 'bg2' : 'bg3'"
                  @login="login"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentPage === 6" class="pop-body mobile">
          <div class="pop-body__inner page-content-box">
            <div class="pop-body__title">
              <span class="icon">💪</span>
              <span class="title">숨겨진 강점 (신살, 오행의 우위)</span>
            </div>
            <div class="pop-body__content">
              <div class="pop-body__tit ganji-txt" :class="[five.getColorFive(ilganChnChar)]">
                {{ sal?.keySentence }}
              </div>

              <div class="pop-body__desc-wrap">
                <div class="tit">조용히 빛나는 나의 가능성</div>
                <div class="txt_wrap">
                  <div v-for="line in sal?.description.value" :key="line" class="txt">
                    {{ line }}
                  </div>
                </div>
              </div>
              <div class="default-wrap" v-if="accessToken">
                <div class="pop-body__desc-wrap">
                  <div class="tit">숨겨진 듯, 그러나 언제나 작동하는 힘</div>
                  <div class="txt_wrap none-bb">
                    <div v-for="line in sal?.detail.value" :key="line" class="txt">
                      {{ line }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="default-wrap" v-else>
                <ComLoginMessage
                  :message="message4"
                  :customClass="isMobile ? 'bg2' : 'bg3'"
                  @login="login"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentPage === 7" class="pop-body mobile">
          <div class="pop-body__inner page-content-box">
            <div
              class="title-saju-reading-finish-box"
              :style="{ paddingBottom: isMobile ? '52px' : '0px' }"
            >
              <div class="txt-box">
                <div class="txt">
                  <span class="icon">📖</span>
                  <div class="saju-pop__outro">
                    사주가 들려주는 나의 이야기,<br />
                    나를 이해하는 가장 깊이 있는 방법<br />
                    <br />
                    사주 속에 숨겨진 ‘관계 속의 나’ 를 알아보세요 🤝<br />
                    나의 성향과 스타일을 한눈에 확인할 수 있어요 👀<br />
                    내가 가진 숨겨진 강점까지 발견할 수 있습니다 💎<br />
                    ...<br /><br />
                    숨겨진 나를 마주하는 특별한 경험<br />
                    <span class="colored-txt">사주를 저장 후 확인</span>하세요.<br />
                    <br />
                    그리고, 지금 그 여정을 시작해 보세요.
                  </div>
                </div>
              </div>

              <button class="bg-button" @click="routeTab">사주풀이 더 자세히 보기</button>
            </div>
          </div>
        </div>

        <div class="pop-pagination" v-if="!infoPage && currentPage !== 0">
          <div class="pop-pagination__index">0{{ currentPage }} / 07</div>
          <button class="pop-pagination__prev" @click="clickPrevPage">
            <i class="icon-chevron"></i>
          </button>
          <button class="pop-pagination__next" @click="clickNextPage">
            <i class="icon-chevron right"></i>
          </button>
        </div>
      </template>
    </div>
  </div>
  <ConfirmComponent
    ref="dialogConfirm"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
.title-saju-reading-finish-box {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  gap: 40px;
  .txt-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .txt {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: var(--Gray-700, #575757);
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 160% */
    .icon {
      color: var(--Gray-800, #333);
      font-family: Pretendard;
      font-size: 28px;
      font-style: normal;
      font-weight: 700;
      line-height: 28px; /* 100% */
    }
  }
}
.pop-body__title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px !important;
  .icon {
    font-size: 28px !important;
    font-weight: 700;
    display: flex;
    width: 44px !important;
    height: 44px;
    align-items: center;
  }
  .title {
    display: flex;
  }
}
.pop-body__desc-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  .tit {
    color: var(--Gray-700, #575757);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 160% */
  }
  .txt_wrap {
    width: 100%;
    .txt {
      color: var(--Gray-700, #575757);
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 160% */
    }
  }
}
.pop-body__tit {
  display: flex;
  padding: 12px 16px;
  align-items: center;
  gap: 0px;
  border-radius: 8px;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  &.five01 {
    background: rgba(194, 53, 53, 0.05);
  }
  &.five02 {
    background: rgba(64, 64, 64, 0.05);
  }
  &.five03 {
    background: rgba(94, 159, 28, 0.05);
  }
  &.five04 {
    background: rgba(148, 148, 148, 0.05);
  }
  &.five05 {
    background: rgba(219, 152, 35, 0.05);
  }
}
.bg-button {
  display: flex;
  width: 100%;
  height: 48px;
  min-width: 80px;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 125% */
}

// .saju-reading-dialog .pop-saju-reading-container .pop-saju-reading-inner {
//   position: relative;
// }
// .saju-reading-dialog .pop-saju-reading-container .pop-saju-reading-inner .pop-body {
//   .pop-body__content {
//     gap: 0;
//     border-radius: 12px;
//   }
//   .pop-body__desc {
//     border-radius: 0;
//   }
// }
.default-inner-box {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.saju-pop__outro {
  color: var(--Gray-700, #575757);
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  .colored-txt {
    color: var(--orange-300-main, #eb4c10);
  }
}
</style>
